'use client'

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react'
import { useForm, type UseFormRegisterReturn } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import {
  ArrowUpDown,
  Calendar,
  Clock,
  Flag,
  GraduationCap,
  Heart,
  Home,
  Pencil,
  PieChart,
  Plus,
  Sunset,
  Target,
  Trash2,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { MetricCard } from '@/features/cases/components/stat-card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ErrorBoundary } from '@/components/errors/error-boundary'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DetailRow } from '@/features/cases/components/detail-row'
import {
  createGoalSchema,
  editGoalDetailsSchema,
  goalFundFormSchema,
  goalPlanNoteFormSchema,
  upsertBuildProfileSchema,
  type CreateGoalFormValues,
  type EditGoalDetailsFormValues,
  type GoalFundFormValues,
  type GoalPlanNoteFormValues,
  type UpsertBuildProfileFormValues,
} from '@/features/cases/schemas/case-forms.schema'
import {
  GOAL_KEY_LABEL,
  GOAL_STATUS_LABEL,
  type CaseBuildProfile,
  type CaseGoal,
  type CaseGoalFund,
  type CreateCaseGoalFundInput,
  type CreateCaseGoalInput,
  type GoalKey,
  type GoalPriority,
  type GoalStatus,
  type UpdateCaseGoalFundInput,
  type UpdateCaseGoalInput,
  type UpsertCaseBuildProfileInput,
} from '@/features/cases/types'
import { formatCurrency, formatHeadlineCurrency } from '@/lib/format/currency'

type GoalFieldKey = 'childName' | 'targetAge' | 'presentCost' | 'monthlyExpense' | 'years'

const GOAL_KEYS: GoalKey[] = ['wealth', 'retirement', 'home', 'education', 'marriage']

const GOAL_ICON: Record<GoalKey, typeof TrendingUp> = {
  wealth: TrendingUp,
  retirement: Sunset,
  home: Home,
  education: GraduationCap,
  marriage: Heart,
}

const GOAL_FIELDS: Record<GoalKey, GoalFieldKey[]> = {
  wealth: ['years'],
  retirement: ['targetAge', 'monthlyExpense'],
  home: ['targetAge', 'presentCost'],
  education: ['childName', 'targetAge', 'presentCost'],
  marriage: ['childName', 'targetAge', 'presentCost'],
}

const FIELD_META: Record<
  GoalFieldKey,
  { label: string; placeholder: string; type: 'text' | 'number' }
> = {
  childName: { label: "Child's name (optional)", placeholder: 'e.g. Ananya', type: 'text' },
  targetAge: { label: 'Target age to achieve this', placeholder: 'e.g. 18', type: 'number' },
  presentCost: { label: 'Present-day cost (₹)', placeholder: 'e.g. 1500000', type: 'number' },
  monthlyExpense: {
    label: 'Present monthly expense needed (₹)',
    placeholder: 'e.g. 60000',
    type: 'number',
  },
  years: { label: 'Years to get there', placeholder: 'e.g. 10', type: 'number' },
}

const FILTERS: { value: 'all' | GoalStatus; label: string }[] = [
  { value: 'all', label: 'All Goals' },
  { value: 'on-track', label: 'On Track' },
  { value: 'at-risk', label: 'At Risk' },
  { value: 'completed', label: 'Completed' },
]

function money(value: string | null): string {
  return value === null ? '—' : formatCurrency(Number(value))
}

function percentFunded(goal: CaseGoal): number | null {
  if (goal.target === null || Number(goal.target) === 0) return null
  const pct = (Number(goal.invested ?? '0') / Number(goal.target)) * 100
  return Math.max(0, Math.min(100, pct))
}

type GoalSort = 'priority' | 'progress' | 'target'

const SORT_OPTIONS: { value: GoalSort; label: string }[] = [
  { value: 'priority', label: 'Sort by: Priority' },
  { value: 'progress', label: 'Sort by: Progress' },
  { value: 'target', label: 'Sort by: Target size' },
]

const PRIORITY_RANK: Record<GoalPriority, number> = { high: 3, medium: 2, low: 1 }
const PRIORITY_LABEL: Record<GoalPriority, string> = { high: 'High', medium: 'Medium', low: 'Low' }

// Matches the reference design's own `GOAL_STATUS_META` — `not-applicable` (wealth, or a target not
// yet computable) deliberately has no entry, so it falls back to a plain neutral Badge instead of a
// fabricated color.
const GOAL_STATUS_COLOR: Partial<Record<GoalStatus, string>> = {
  'on-track': 'var(--chart-positive)',
  'at-risk': 'var(--financial-status-fair)',
  completed: 'var(--primary)',
}

function StatusPill({ status }: { status: GoalStatus }) {
  const color = GOAL_STATUS_COLOR[status]
  if (!color) {
    return <Badge variant="outline">{GOAL_STATUS_LABEL[status]}</Badge>
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold"
      style={{
        backgroundColor: `color-mix(in oklch, ${color} 12%, transparent)`,
        borderColor: `color-mix(in oklch, ${color} 24%, transparent)`,
        color,
      }}
    >
      <span className="size-1.5 shrink-0 rounded-full" style={{ backgroundColor: color }} />
      {GOAL_STATUS_LABEL[status]}
    </span>
  )
}

/** The ticket header's sub-line — always "N years" for every `GoalKey`, standardized (previously
 * showed "Age N" for retirement/education/marriage, mixing formats with the "N years" used for
 * home/wealth — inconsistent, and not even accurate: a bare age number isn't a duration). Uses the
 * already-computed `yearsToGoal` (real years remaining, not the raw stored `targetAge`) for every
 * type except `wealth`, which has no age dependency and uses its own `years` input directly. Empty
 * until `yearsToGoal` is computable (investor age not set yet) — never a fabricated number. */
function goalHorizonText(goal: CaseGoal): string {
  if (goal.key === 'wealth') return goal.years ? `${goal.years} years` : ''
  return goal.yearsToGoal ? `${goal.yearsToGoal} years` : ''
}

/** The dialog's read-only detail rows, one set per `GoalKey` — the exact per-type breakdown table
 * from the feature plan. `home` shows the derived horizon (years), `education`/`marriage` show the
 * raw target age instead — a presentation choice only (both store the same `targetAge` field and
 * compute the same `yearsToGoal`), matching each goal type's own reference screenshot. The
 * "Target" figure is exactly what was entered (present cost, or 12x monthly expense × the
 * retirement corpus multiplier) unless staff has opted this goal into `inflationRate`, in which
 * case it's that figure compounded forward over `yearsToGoal` — computed server-side either way,
 * never re-derived here. An "Inflation rate" row is added for the 4 non-`wealth` types, showing
 * "Not set" (never a fabricated "0%") when `inflationRate` is `null`. */
function goalDetailRows(goal: CaseGoal): { label: string; value: string }[] {
  const targetText = goal.target === null ? 'Not set yet' : money(goal.target)
  const inflationRow = {
    label: 'Inflation rate',
    value: goal.inflationRate === null ? 'Not set' : `${(goal.inflationRate * 100).toFixed(2)}%`,
  }
  switch (goal.key) {
    case 'wealth':
      return [{ label: 'Horizon', value: goal.years ? `${goal.years} years` : '—' }]
    case 'retirement':
      return [
        { label: 'Target', value: targetText },
        { label: 'Monthly expense (today)', value: money(goal.monthlyExpense) },
        { label: 'Target age', value: goal.targetAge ? String(goal.targetAge) : '—' },
        inflationRow,
      ]
    case 'home':
      return [
        { label: 'Target', value: targetText },
        { label: 'Present cost', value: money(goal.presentCost) },
        { label: 'Horizon', value: goal.yearsToGoal ? `${goal.yearsToGoal} years` : '—' },
        inflationRow,
      ]
    case 'education':
    case 'marriage':
      return [
        { label: 'Target', value: targetText },
        { label: 'Present cost', value: money(goal.presentCost) },
        { label: 'Target age', value: goal.targetAge ? String(goal.targetAge) : '—' },
        inflationRow,
      ]
  }
}

// Every comparator sorts descending (highest priority/progress/target first) — unset values sink
// to the bottom rather than throwing off goals that do have a value.
const GOAL_SORT_COMPARATOR: Record<GoalSort, (a: CaseGoal, b: CaseGoal) => number> = {
  priority: (a, b) =>
    (b.priority ? PRIORITY_RANK[b.priority] : 0) - (a.priority ? PRIORITY_RANK[a.priority] : 0),
  progress: (a, b) => (percentFunded(b) ?? -1) - (percentFunded(a) ?? -1),
  target: (a, b) => Number(b.target ?? 0) - Number(a.target ?? 0),
}

/**
 * Build Portfolio's goal-tracking sub-resource — parallel to `case-analysis.tsx` (Holdings/Trades)
 * for the other 2 service types, replacing it in `client-detail.tsx` when
 * `caseDetail.serviceType === 'build_portfolio'`. Shared by staff (who author it) and, from Phase 5,
 * the investor dashboard's read-only view — `canEdit` is the only thing that differs, same
 * convention as `CasePrescription`.
 */
export function CaseGoals(props: {
  caseId: string
  canEdit?: boolean
  /** Whether the Financial Profile card renders at all — distinct from `canEdit`, since that flag
   * also covers the investor's own read-only view, where this card was deliberately never shown
   * (see the card's own comment). Staff and admin should both see it read-only; only investors
   * shouldn't. Defaults to `canEdit` so every existing caller keeps its current behavior unless it
   * opts in explicitly. */
  showFinancialProfile?: boolean
  goals: CaseGoal[]
  buildProfile: CaseBuildProfile | null
  /** Drives whether a goal's target/years-to-goal can even be computed — see `EMPTY_PROFILE`'s
   * sibling prompt below for the age-not-set case, mirrored from the backend's own
   * "never a wrong number, always an honest can't-compute-yet state" rule. */
  investorAge: number | null
  onAddGoal?: (input: CreateCaseGoalInput) => Promise<void>
  onEditGoal?: (id: string, input: UpdateCaseGoalInput) => Promise<void>
  onRemoveGoal?: (id: string) => void
  onSetGoalApproved?: (id: string, approved: boolean) => void
  onAddGoalFund?: (goalId: string, input: CreateCaseGoalFundInput) => Promise<void>
  onUpdateGoalFund?: (
    goalId: string,
    fundId: string,
    input: UpdateCaseGoalFundInput,
  ) => Promise<void>
  onRemoveGoalFund?: (goalId: string, fundId: string) => Promise<void>
  onUpsertBuildProfile?: (input: UpsertCaseBuildProfileInput) => Promise<void>
}) {
  return (
    <ErrorBoundary label="this investor's goals" resetKeys={[props.caseId]}>
      <CaseGoalsBody {...props} />
    </ErrorBoundary>
  )
}

function CaseGoalsBody({
  canEdit = false,
  showFinancialProfile = canEdit,
  goals,
  buildProfile,
  investorAge,
  onAddGoal,
  onEditGoal,
  onRemoveGoal,
  onSetGoalApproved,
  onAddGoalFund,
  onUpdateGoalFund,
  onRemoveGoalFund,
  onUpsertBuildProfile,
}: {
  caseId: string
  canEdit?: boolean
  showFinancialProfile?: boolean
  goals: CaseGoal[]
  buildProfile: CaseBuildProfile | null
  investorAge: number | null
  onAddGoal?: (input: CreateCaseGoalInput) => Promise<void>
  onEditGoal?: (id: string, input: UpdateCaseGoalInput) => Promise<void>
  onRemoveGoal?: (id: string) => void
  onSetGoalApproved?: (id: string, approved: boolean) => void
  onAddGoalFund?: (goalId: string, input: CreateCaseGoalFundInput) => Promise<void>
  onUpdateGoalFund?: (
    goalId: string,
    fundId: string,
    input: UpdateCaseGoalFundInput,
  ) => Promise<void>
  onRemoveGoalFund?: (goalId: string, fundId: string) => Promise<void>
  onUpsertBuildProfile?: (input: UpsertCaseBuildProfileInput) => Promise<void>
}) {
  const [addingGoal, setAddingGoal] = useState(false)
  // An id, not the whole goal object — so the dialog always reflects the latest fetched goal (e.g.
  // its updated target/status/plan right after a save), not a stale snapshot from click time.
  const [selectedGoalId, setSelectedGoalId] = useState<string | null>(null)
  const selectedGoal = goals.find((g) => g.id === selectedGoalId) ?? null
  const [editingProfile, setEditingProfile] = useState(false)
  const [filter, setFilter] = useState<'all' | GoalStatus>('all')
  const [sort, setSort] = useState<GoalSort>('priority')

  const shown = useMemo(() => {
    const filtered = filter === 'all' ? goals : goals.filter((g) => g.status === filter)
    return [...filtered].sort(GOAL_SORT_COMPARATOR[sort])
  }, [goals, filter, sort])

  // "Total Monthly Target" per the reference design — same computation as this app's own earlier
  // "Total Monthly SIP" metric, just relabeled/re-tinted to match (confirmed by hand-checking the
  // design's own screenshot math: ₹78,000 = the sum of exactly the goals with a SIP set). Each
  // goal's own `totalMonthlySip` is already the sum of its funds (`case.mapper.ts`), so this is a
  // sum-of-sums, not a re-derivation of any fund-level math.
  const totalMonthlySip = goals.reduce((sum, g) => sum + Number(g.totalMonthlySip), 0)
  const onTrackCount = goals.filter((g) => g.status === 'on-track').length
  // Average % funded across goals with a computable target — excludes `wealth` (never has one) and
  // any goal still `not-applicable` (investor age not set yet), same exclusion the design applies.
  const goalsWithProgress = goals
    .map((g) => percentFunded(g))
    .filter((pct): pct is number => pct !== null)
  const avgProgress =
    goalsWithProgress.length > 0
      ? goalsWithProgress.reduce((sum, pct) => sum + pct, 0) / goalsWithProgress.length
      : null

  const surplus = buildProfile
    ? Number(buildProfile.monthlyIncome) -
      (Number(buildProfile.rentEmi) +
        Number(buildProfile.utilitiesGroceries) +
        Number(buildProfile.transportLifestyle) +
        Number(buildProfile.otherExpenses))
    : 0

  return (
    <div className="flex flex-col gap-4">
      {/* Financial profile — the intake wizard's own income/expense breakdown. It has no equivalent
          anywhere in the reference design (staff or investor side), and the investor themselves
          already knows their own numbers — this app added it purely so staff/admin have it
          alongside the goals to size a realistic total SIP, so it must never render for the
          investor's own read-only view (`showFinancialProfile={false}` there). Edit affordances
          below key off `onUpsertBuildProfile` itself (only ever passed for staff), not this flag,
          so admin sees the card read-only without any extra gating needed here. */}
      {showFinancialProfile && (
        <div className="min-w-0 rounded-lg border p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <p className="text-sm font-semibold">Financial profile</p>
            {onUpsertBuildProfile && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-muted-foreground size-7"
                aria-label="Edit financial profile"
                onClick={() => setEditingProfile(true)}
              >
                <Pencil className="size-3.5" />
              </Button>
            )}
          </div>
          {buildProfile ? (
            <div className="flex flex-col gap-2">
              <DetailRow label="Monthly income" value={money(buildProfile.monthlyIncome)} />
              <DetailRow label="Rent / EMI" value={money(buildProfile.rentEmi)} />
              <DetailRow
                label="Utilities & groceries"
                value={money(buildProfile.utilitiesGroceries)}
              />
              <DetailRow
                label="Transport & lifestyle"
                value={money(buildProfile.transportLifestyle)}
              />
              <DetailRow label="Loans, cards & other" value={money(buildProfile.otherExpenses)} />
              <div className="bg-muted flex items-center justify-between gap-3 rounded-lg px-3.5 py-2.5">
                <span className="text-xs font-semibold">Monthly surplus</span>
                <span
                  className={`text-xs font-bold tabular-nums ${surplus >= 0 ? 'text-green-600' : 'text-red-600'}`}
                >
                  {formatCurrency(surplus)}
                </span>
              </div>
            </div>
          ) : (
            <Empty className="border border-dashed py-6">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Wallet />
                </EmptyMedia>
                <EmptyTitle>No financial profile yet</EmptyTitle>
                <EmptyDescription>
                  Add the income/expense breakdown to start sizing this plan.
                </EmptyDescription>
              </EmptyHeader>
              {onUpsertBuildProfile && (
                <Button size="sm" className="mx-auto w-fit" onClick={() => setEditingProfile(true)}>
                  Add financial profile
                </Button>
              )}
            </Empty>
          )}
        </div>
      )}

      {/* Staff-only: only staff can act on this (via "Edit investor details"), and the investor
          has no such control — showing it to them was just unactionable noise. */}
      {canEdit && investorAge === null && goals.length > 0 && (
        <p className="text-muted-foreground bg-muted rounded-lg px-3.5 py-2.5 text-xs">
          Set the investor&apos;s age (Edit investor details) to show each goal&apos;s years
          remaining — a goal&apos;s target amount is always shown as entered and doesn&apos;t need
          this.
        </p>
      )}

      {/* Metrics row — matches the reference design's exact 4 cards (Total Goals/Total Monthly
          Target/On Track/Avg. Progress). Approved-plan and At-Risk counts aren't dropped
          information, just moved: a goal's plan status is already a badge on its card, and At Risk
          is one of the filter tabs below. */}
      {goals.length > 0 && (
        <div className="@lg/main:grid-cols-4 grid grid-cols-1 gap-2">
          <MetricCard
            icon={<Target />}
            tint="var(--chart-2)"
            label="Total Goals"
            value={goals.length}
            footnote="Active goals"
          />
          <MetricCard
            icon={<TrendingUp />}
            tint="var(--chart-positive)"
            label="Total Monthly Target"
            value={formatHeadlineCurrency(totalMonthlySip)}
            title={formatCurrency(totalMonthlySip)}
            footnote="Across all goals"
          />
          <MetricCard
            icon={<PieChart />}
            tint="var(--chart-1)"
            label="On Track"
            value={onTrackCount}
            footnote={`${Math.round((onTrackCount / goals.length) * 100)}% of goals`}
          />
          <MetricCard
            icon={<Clock />}
            tint="var(--chart-4)"
            label="Avg. Progress"
            value={avgProgress === null ? '—' : `${Math.round(avgProgress)}%`}
            footnote={
              avgProgress === null
                ? 'No progress to show yet'
                : avgProgress >= 100
                  ? 'All funded!'
                  : 'Keep going!'
            }
          />
        </div>
      )}

      {goals.length === 0 ? (
        <Empty className="border border-dashed py-8">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Target />
            </EmptyMedia>
            <EmptyTitle>No goals captured</EmptyTitle>
            <EmptyDescription>
              {canEdit
                ? 'Add this investor’s goals to start building their plan.'
                : "Your advisor hasn't captured any goals for this plan yet."}
            </EmptyDescription>
          </EmptyHeader>
          {canEdit && onAddGoal && (
            <Button size="sm" className="mx-auto w-fit" onClick={() => setAddingGoal(true)}>
              Add goal
            </Button>
          )}
        </Empty>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Tabs value={filter} onValueChange={(v) => setFilter(v as 'all' | GoalStatus)}>
              <TabsList variant="line">
                {FILTERS.map((f) => (
                  <TabsTrigger key={f.value} value={f.value}>
                    {f.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Select value={sort} onValueChange={(v) => setSort(v as GoalSort)}>
                <SelectTrigger size="sm" className="w-[180px] text-xs">
                  <ArrowUpDown className="size-3.5" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canEdit && onAddGoal && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  // `size="sm"` alone is h-7, one pixel shorter than the sort Select's own
                  // `size="sm"` (h-8) — overridden here so the two sit at exactly the same
                  // height side by side.
                  className="h-8"
                  onClick={() => setAddingGoal(true)}
                >
                  Add goal
                </Button>
              )}
            </div>
          </div>

          <ol className="@xl/main:grid-cols-2 @4xl/main:grid-cols-3 grid grid-cols-1 gap-3">
            {shown.map((goal) => {
              const Icon = GOAL_ICON[goal.key]
              const pct = percentFunded(goal)
              const horizon = goalHorizonText(goal)
              const progressColor = GOAL_STATUS_COLOR[goal.status] ?? 'var(--primary)'
              return (
                <li
                  key={goal.id}
                  data-testid="goal-ticket"
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedGoalId(goal.id)}
                  onKeyDown={(e: KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setSelectedGoalId(goal.id)
                    }
                  }}
                  // Naturally sized to its own content, not stretched/padded to match any other
                  // card — matches the reference design's own compact card exactly. The whole
                  // ticket (not just the header) opens the dialog; the card holds no other
                  // interactive elements anymore (all actions moved into that dialog in Phase
                  // 6.5), so there's no nested-interactive-control conflict in making it one.
                  className="flex cursor-pointer flex-col gap-2 rounded-[22px] p-1.5"
                  style={{ backgroundColor: 'var(--color-blue-600)' }}
                >
                  {/* Header sits directly on the ticket's own blue background (no separate rounded
                      corners of its own) — deliberately a fixed blue rather than `--primary` (which
                      lightens in dark mode), matching the reference design's own reasoning for
                      keeping this stub visually consistent regardless of theme. */}
                  <div className="flex min-w-0 items-center justify-between gap-2 px-2 pt-1 text-white">
                    <span className="flex min-w-0 items-center gap-2">
                      <Icon className="size-4 shrink-0" />
                      <span className="truncate text-sm font-semibold">
                        {GOAL_KEY_LABEL[goal.key]}
                        {goal.childName ? ` · ${goal.childName}` : ''}
                      </span>
                    </span>
                    {horizon && <span className="shrink-0 text-xs opacity-90">{horizon}</span>}
                  </div>

                  <div className="bg-card flex flex-col gap-3 rounded-[18px] p-3.5">
                    <div className="flex flex-wrap items-center justify-between gap-1.5">
                      <StatusPill status={goal.status} />
                      {pct !== null && <Badge variant="outline">{pct.toFixed(1)}%</Badge>}
                    </div>

                    {goal.key === 'wealth' ? (
                      // Same bold-headline + muted-subtext treatment as every other goal type,
                      // for visual consistency — just no "invested of X target" comparison or
                      // progress bar, since Wealth Creation never has a fixed target to compare
                      // against (tracks the invested amount alone, per goal-math.ts).
                      <div>
                        <p className="text-xl font-bold tabular-nums">{money(goal.invested)}</p>
                        <p className="text-muted-foreground text-xs">Invested so far</p>
                      </div>
                    ) : (
                      <>
                        <div>
                          <p className="text-xl font-bold tabular-nums">
                            {money(goal.target)}
                            {/* Read-only context on how the target was derived — shown to every
                                role (investor included), never gated behind `canEdit`, unlike the
                                staff-only editing controls elsewhere on this card. */}
                            {goal.inflationRate !== null && (
                              <span className="text-muted-foreground ml-1.5 text-xs font-normal">
                                +{(goal.inflationRate * 100).toFixed(1)}%/yr inflation
                              </span>
                            )}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {money(goal.invested)} invested of {money(goal.target)} target
                          </p>
                        </div>
                        {pct !== null && (
                          <div className="bg-muted h-1.5 overflow-hidden rounded-full">
                            <div
                              className="h-full rounded-full transition-[width]"
                              style={{ width: `${pct}%`, backgroundColor: progressColor }}
                            />
                          </div>
                        )}
                      </>
                    )}

                    <div className="border-t border-dashed" />

                    {/* 2-column footer (Monthly SIP + Priority), matching the reference design.
                        The recommended fund name (and every other plan/edit detail) lives only in
                        the dialog now — the card is a summary, not a second place to show it. */}
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
                          <Calendar className="size-3.5" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">
                            {goal.funds.length > 0 ? money(goal.totalMonthlySip) : 'Not set'}
                          </p>
                          <p className="text-muted-foreground text-xs">Monthly SIP</p>
                        </div>
                      </div>
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="bg-primary/10 text-primary flex size-8 shrink-0 items-center justify-center rounded-lg">
                          <Flag className="size-3.5" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold">
                            {goal.priority ? PRIORITY_LABEL[goal.priority] : 'Not set'}
                          </p>
                          <p className="text-muted-foreground text-xs">Priority</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ol>
        </>
      )}

      {onAddGoal && (
        <AddGoalDialog open={addingGoal} onOpenChange={setAddingGoal} onSubmit={onAddGoal} />
      )}
      <GoalDetailDialog
        goal={selectedGoal}
        canEdit={canEdit}
        open={!!selectedGoal}
        onOpenChange={(open) => !open && setSelectedGoalId(null)}
        onSubmit={onEditGoal}
        onSetGoalApproved={onSetGoalApproved}
        onAddGoalFund={onAddGoalFund}
        onUpdateGoalFund={onUpdateGoalFund}
        onRemoveGoalFund={onRemoveGoalFund}
        onRemoveGoal={
          onRemoveGoal
            ? (id) => {
                onRemoveGoal(id)
                setSelectedGoalId(null)
              }
            : undefined
        }
      />
      {onUpsertBuildProfile && (
        <BuildProfileDialog
          buildProfile={buildProfile}
          open={editingProfile}
          onOpenChange={setEditingProfile}
          onSubmit={onUpsertBuildProfile}
        />
      )}
    </div>
  )
}

function GoalFieldInputs({
  goalKey,
  register,
  errors,
}: {
  goalKey: GoalKey
  register: (field: GoalFieldKey) => UseFormRegisterReturn
  errors: Partial<Record<GoalFieldKey, { message?: string } | undefined>>
}) {
  return (
    <>
      {GOAL_FIELDS[goalKey].map((field) => {
        const meta = FIELD_META[field]
        return (
          <Field key={field}>
            <FieldLabel htmlFor={`goal-${field}`}>{meta.label}</FieldLabel>
            <Input
              id={`goal-${field}`}
              type={meta.type}
              inputMode={meta.type === 'number' ? 'numeric' : undefined}
              placeholder={meta.placeholder}
              {...register(field)}
            />
            <FieldError errors={[errors[field]]} />
          </Field>
        )
      })}
    </>
  )
}

function AddGoalDialog({
  open,
  onOpenChange,
  onSubmit: onSubmitProp,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: CreateCaseGoalInput) => Promise<void>
}) {
  const [goalKey, setGoalKey] = useState<GoalKey>('wealth')
  const form = useForm<z.input<typeof createGoalSchema>, unknown, CreateGoalFormValues>({
    resolver: zodResolver(createGoalSchema),
    mode: 'onTouched',
    defaultValues: { key: 'wealth' },
  })

  const changeKey = (key: GoalKey) => {
    setGoalKey(key)
    form.reset({ key } as z.input<typeof createGoalSchema>)
  }

  const onSubmit = form.handleSubmit(async (values) => {
    await onSubmitProp(values as CreateCaseGoalInput)
    onOpenChange(false)
    form.reset({ key: 'wealth' })
    setGoalKey('wealth')
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) {
          form.reset({ key: 'wealth' })
          setGoalKey('wealth')
        }
      }}
    >
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Add goal</DialogTitle>
            <DialogDescription>What is this investor investing for?</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="add-goal-key">Goal type</FieldLabel>
              <Select value={goalKey} onValueChange={(v) => changeKey(v as GoalKey)}>
                <SelectTrigger id="add-goal-key" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GOAL_KEYS.map((key) => (
                    <SelectItem key={key} value={key}>
                      {GOAL_KEY_LABEL[key]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <GoalFieldInputs
              goalKey={goalKey}
              register={(field) => form.register(field as never)}
              errors={form.formState.errors as Partial<Record<GoalFieldKey, { message?: string }>>}
            />
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Adding…' : 'Add goal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const PRIORITY_OPTIONS: GoalPriority[] = ['high', 'medium', 'low']

/** Read-only fund list — used by both the investor's view and staff's collapsed (non-editing)
 * view. "No funds recommended yet" only shows up here in the investor's collapsed view in
 * practice (staff sees the add-fund form instead once a plan has zero funds), but is included for
 * completeness rather than assuming the caller never renders this with an empty list. */
function GoalFundsReadList({
  funds,
  totalMonthlySip,
}: {
  funds: CaseGoalFund[]
  totalMonthlySip: string
}) {
  if (funds.length === 0) {
    return <p className="text-muted-foreground text-xs">No funds recommended yet.</p>
  }
  return (
    <div className="flex flex-col gap-1.5">
      {funds.map((fund) => (
        <DetailRow key={fund.id} label={fund.fundName} value={money(fund.monthlySip)} />
      ))}
      <TotalMonthlySipRow totalMonthlySip={totalMonthlySip} />
    </div>
  )
}

/** Same pill shape as `DetailRow` (matches the reference design), but accent-tinted — this is a
 * sum across every fund above it, not just another fund, so it's deliberately visually distinct
 * rather than blending into the list. */
function TotalMonthlySipRow({ totalMonthlySip }: { totalMonthlySip: string }) {
  return (
    <div className="bg-primary/10 flex items-center justify-between gap-3 rounded-lg px-3.5 py-2.5">
      <span className="text-primary text-xs font-semibold">Total Monthly SIP</span>
      <span className="text-primary min-w-0 truncate text-xs font-bold tabular-nums">
        {money(totalMonthlySip)}
      </span>
    </div>
  )
}

/** One fund's edit row — its own uncontrolled-ish local state, saved via `onUpdate` on blur only
 * when a value actually changed (mirrors this app's existing "save on blur" inline-edit
 * convention, e.g. `client-detail.tsx`'s inline Qualified-status control triggering on change, not
 * on every keystroke). Each fund is its own dedicated endpoint/mutation — editing one never
 * touches any other fund on the same goal. */
function GoalFundEditRow({
  fund,
  onUpdate,
  onRemove,
}: {
  fund: CaseGoalFund
  onUpdate?: (input: UpdateCaseGoalFundInput) => Promise<void>
  onRemove?: () => Promise<void>
}) {
  const [fundName, setFundName] = useState(fund.fundName)
  const [monthlySip, setMonthlySip] = useState(fund.monthlySip)

  const saveIfChanged = () => {
    if (!onUpdate) return
    const input: UpdateCaseGoalFundInput = {}
    if (fundName.trim() && fundName !== fund.fundName) input.fundName = fundName
    const nextSip = Number(monthlySip)
    if (monthlySip.trim() && !Number.isNaN(nextSip) && nextSip !== Number(fund.monthlySip)) {
      input.monthlySip = nextSip.toFixed(2)
    }
    if (Object.keys(input).length > 0) void onUpdate(input)
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        aria-label={`Fund name — ${fund.fundName}`}
        value={fundName}
        onChange={(e) => setFundName(e.target.value)}
        onBlur={saveIfChanged}
        className="min-w-0 flex-1"
      />
      <Input
        aria-label={`Monthly SIP — ${fund.fundName}`}
        type="number"
        inputMode="numeric"
        value={monthlySip}
        onChange={(e) => setMonthlySip(e.target.value)}
        onBlur={saveIfChanged}
        className="w-28 shrink-0"
      />
      {onRemove && (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
          aria-label={`Remove ${fund.fundName}`}
          onClick={() => void onRemove()}
        >
          <Trash2 className="size-3.5" />
        </Button>
      )}
    </div>
  )
}

/**
 * The pixel-perfect goal dialog — port of the reference design's single shared `Dialog` +
 * `GoalPlanSection` (`CaseAnalysis.tsx`), used by both roles (staff and the investor's read-only
 * view) via `canEdit`, same convention as everything else in this file. "Edit details" (base
 * fields + invested/priority/inflationRate — has no equivalent in the design, added on top of it
 * since staff needs to correct these after intake) is one independent form; "Plan for this goal"
 * is now a repeatable fund list (each fund added/edited/removed through its own dedicated
 * endpoint/mutation, never bundled into one submit) plus a separate note field. Editing details
 * never touches the plan, so editing a target age can't accidentally reset it back to pending
 * review (the backend only resets `planStatus` when a fund is added/edited/removed, or `planNote`
 * is present in an `updateGoal` request).
 */
function GoalDetailDialog({
  goal,
  canEdit,
  open,
  onOpenChange,
  onSubmit: onSubmitProp,
  onSetGoalApproved,
  onAddGoalFund,
  onUpdateGoalFund,
  onRemoveGoalFund,
  onRemoveGoal,
}: {
  goal: CaseGoal | null
  canEdit: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit?: (id: string, input: UpdateCaseGoalInput) => Promise<void>
  onSetGoalApproved?: (id: string, approved: boolean) => void
  onAddGoalFund?: (goalId: string, input: CreateCaseGoalFundInput) => Promise<void>
  onUpdateGoalFund?: (
    goalId: string,
    fundId: string,
    input: UpdateCaseGoalFundInput,
  ) => Promise<void>
  onRemoveGoalFund?: (goalId: string, fundId: string) => Promise<void>
  onRemoveGoal?: (id: string) => void
}) {
  const [editingDetails, setEditingDetails] = useState(false)
  const [editingPlan, setEditingPlan] = useState(false)
  const [addingFund, setAddingFund] = useState(false)
  const [confirmingRemove, setConfirmingRemove] = useState(false)

  const detailsForm = useForm<
    z.input<typeof editGoalDetailsSchema>,
    unknown,
    EditGoalDetailsFormValues
  >({
    resolver: zodResolver(editGoalDetailsSchema),
    mode: 'onTouched',
    values: goal
      ? ({
          key: goal.key,
          childName: goal.childName ?? undefined,
          targetAge: goal.targetAge ?? undefined,
          presentCost: goal.presentCost ?? undefined,
          monthlyExpense: goal.monthlyExpense ?? undefined,
          years: goal.years ?? undefined,
          invested: goal.invested ?? undefined,
          // Displayed as a percentage (e.g. 6), stored/submitted as a rate (e.g. 0.06) — the form
          // field's own unit, converted back on the way in the same way it's converted on the way
          // out (`optionalInflationRatePercentField`'s `/ 100` transform).
          inflationRate: goal.inflationRate === null ? undefined : goal.inflationRate * 100,
          priority: goal.priority ?? undefined,
        } as unknown as z.input<typeof editGoalDetailsSchema>)
      : undefined,
  })

  const noteForm = useForm<z.input<typeof goalPlanNoteFormSchema>, unknown, GoalPlanNoteFormValues>(
    {
      resolver: zodResolver(goalPlanNoteFormSchema),
      mode: 'onTouched',
      values: { planNote: goal?.planNote ?? '' },
    },
  )

  const addFundForm = useForm<z.input<typeof goalFundFormSchema>, unknown, GoalFundFormValues>({
    resolver: zodResolver(goalFundFormSchema),
    mode: 'onTouched',
    defaultValues: { fundName: '', monthlySip: '' },
  })

  // Reset the dialog's local UI-mode flags every time a *different* goal is opened, so leftover
  // edit-mode state from a previously viewed goal never leaks onto the next one. A never-yet-
  // funded plan opens straight into edit mode for staff, matching the reference design's own
  // `useState(!plan)` default.
  useEffect(() => {
    const noFundsYet = (goal?.funds.length ?? 0) === 0
    setEditingDetails(false)
    setEditingPlan(canEdit && noFundsYet)
    // A never-yet-funded plan opens the add-fund form immediately too — staff shouldn't need an
    // extra click just to see the fields when there's nothing to show yet.
    setAddingFund(canEdit && noFundsYet)
    setConfirmingRemove(false)
    addFundForm.reset({ fundName: '', monthlySip: '' })
    // `canEdit` never changes for a mounted dialog, and re-running this on every `funds` change
    // (e.g. right after this same effect's own `setEditingPlan` closes edit mode) would fight the
    // user's own in-progress edit — deliberately keyed only on the goal id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goal?.id])

  if (!goal) return null

  const Icon = GOAL_ICON[goal.key]
  const hasPlan = goal.funds.length > 0

  const onSubmitDetails = detailsForm.handleSubmit(async (values) => {
    if (!onSubmitProp) return
    await onSubmitProp(goal.id, {
      childName: 'childName' in values ? values.childName : undefined,
      targetAge: 'targetAge' in values ? values.targetAge : undefined,
      presentCost: 'presentCost' in values ? values.presentCost : undefined,
      monthlyExpense: 'monthlyExpense' in values ? values.monthlyExpense : undefined,
      years: 'years' in values ? values.years : undefined,
      invested: values.invested,
      // A blank field alone is ambiguous — it means either "leave unchanged" (never touched) or
      // "clear it" (staff blanked out a previously-set rate). `undefined` from the schema can't
      // tell those apart, so fall back to `goal.inflationRate` itself: only send an explicit
      // `null` (which Prisma writes as an actual NULL, unlike `undefined`, which the backend
      // treats as "field absent, don't touch it") when the goal actually had a rate to clear.
      inflationRate:
        values.inflationRate !== undefined
          ? values.inflationRate
          : goal.inflationRate !== null
            ? null
            : undefined,
      priority: values.priority,
    })
    setEditingDetails(false)
  })

  const onSubmitNote = noteForm.handleSubmit(async (values) => {
    if (!onSubmitProp) return
    await onSubmitProp(goal.id, { planNote: values.planNote })
  })

  const onSubmitAddFund = addFundForm.handleSubmit(async (values) => {
    if (!onAddGoalFund) return
    await onAddGoalFund(goal.id, values)
    addFundForm.reset({ fundName: '', monthlySip: '' })
    setAddingFund(false)
  })

  const confirmRemove = () => {
    if (onRemoveGoal) onRemoveGoal(goal.id)
    setConfirmingRemove(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* This dialog's content is the tallest of any in this feature — 3 read-only rows, an
          optional base-field edit form, and the Plan section's own view/edit state, any of which
          can push it past the viewport on a shorter screen. Fixed max-height + a scrollable middle
          keeps the header/footer always visible instead of letting the dialog get clipped. */}
        <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Icon className="text-primary size-4" />
              {GOAL_KEY_LABEL[goal.key]}
              {goal.childName ? ` · ${goal.childName}` : ''}
            </DialogTitle>
            <DialogDescription>What you&apos;re investing toward, in full.</DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-2 overflow-y-auto py-2 pr-1">
            <div className="flex flex-col gap-2">
              {goalDetailRows(goal).map((row) => (
                <DetailRow key={row.label} label={row.label} value={row.value} />
              ))}
            </div>

            {canEdit && (onSubmitProp || onRemoveGoal) && (
              <div className="border-t pt-3">
                {!editingDetails ? (
                  <div className="flex items-center justify-between gap-2">
                    {onSubmitProp && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingDetails(true)}
                      >
                        <Pencil className="size-3.5" />
                        Edit details
                      </Button>
                    )}
                    {onRemoveGoal && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => setConfirmingRemove(true)}
                      >
                        <Trash2 className="size-3.5" />
                        Remove goal
                      </Button>
                    )}
                  </div>
                ) : (
                  <form onSubmit={onSubmitDetails} className="flex flex-col gap-3">
                    <GoalFieldInputs
                      goalKey={goal.key}
                      register={(field) => detailsForm.register(field as never)}
                      errors={
                        detailsForm.formState.errors as Partial<
                          Record<GoalFieldKey, { message?: string }>
                        >
                      }
                    />
                    <Field>
                      <FieldLabel htmlFor="goal-detail-invested">Invested so far (₹)</FieldLabel>
                      <Input
                        id="goal-detail-invested"
                        type="number"
                        inputMode="numeric"
                        placeholder="e.g. 250000"
                        {...detailsForm.register('invested')}
                      />
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="goal-detail-priority">Priority</FieldLabel>
                      <Select
                        value={detailsForm.watch('priority') ?? undefined}
                        onValueChange={(v) => detailsForm.setValue('priority', v as GoalPriority)}
                      >
                        <SelectTrigger id="goal-detail-priority" className="w-full">
                          <SelectValue placeholder="Not set" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITY_OPTIONS.map((p) => (
                            <SelectItem key={p} value={p}>
                              {PRIORITY_LABEL[p]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field>
                      <FieldLabel htmlFor="goal-detail-inflation-rate">
                        Inflation rate (%, optional)
                      </FieldLabel>
                      <Input
                        id="goal-detail-inflation-rate"
                        type="number"
                        inputMode="numeric"
                        placeholder="e.g. 6"
                        {...detailsForm.register('inflationRate')}
                      />
                      <FieldError errors={[detailsForm.formState.errors.inflationRate]} />
                    </Field>
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={detailsForm.formState.isSubmitting}>
                        {detailsForm.formState.isSubmitting ? 'Saving…' : 'Save'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingDetails(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            )}

            <div className="border-t pt-3">
              <p className="text-muted-foreground mb-2 text-xs font-medium">
                {canEdit ? 'Plan for this goal' : "Your advisor's plan"}
              </p>

              {!canEdit && !hasPlan && (
                <p className="text-muted-foreground text-xs">
                  Your advisor hasn&apos;t attached a plan to this goal yet.
                </p>
              )}

              {!canEdit && hasPlan && (
                <div className="flex flex-col gap-2">
                  <Badge
                    variant={goal.planStatus === 'approved' ? 'default' : 'secondary'}
                    className="w-fit"
                  >
                    {goal.planStatus === 'approved' ? 'On track' : 'Pending review'}
                  </Badge>
                  <GoalFundsReadList funds={goal.funds} totalMonthlySip={goal.totalMonthlySip} />
                  {goal.planNote && (
                    <p className="text-muted-foreground text-xs">{goal.planNote}</p>
                  )}
                </div>
              )}

              {canEdit && !editingPlan && hasPlan && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant={goal.planStatus === 'approved' ? 'default' : 'secondary'}>
                      {goal.planStatus === 'approved' ? 'Approved' : 'Pending review'}
                    </Badge>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setEditingPlan(true)}
                    >
                      Edit
                    </Button>
                  </div>
                  <GoalFundsReadList funds={goal.funds} totalMonthlySip={goal.totalMonthlySip} />
                  {goal.planNote && (
                    <p className="text-muted-foreground text-xs">{goal.planNote}</p>
                  )}
                  {onSetGoalApproved &&
                    (goal.planStatus === 'pending' ? (
                      <Button
                        type="button"
                        size="sm"
                        className="w-fit"
                        onClick={() => onSetGoalApproved(goal.id, true)}
                      >
                        Approve
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="w-fit"
                        onClick={() => onSetGoalApproved(goal.id, false)}
                      >
                        Unapprove
                      </Button>
                    ))}
                </div>
              )}

              {canEdit && (editingPlan || !hasPlan) && (
                <div className="flex flex-col gap-3">
                  {goal.funds.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 px-1">
                        <span className="text-muted-foreground min-w-0 flex-1 text-xs font-medium">
                          Fund Name
                        </span>
                        <span className="text-muted-foreground w-28 shrink-0 text-xs font-medium">
                          SIP Amount
                        </span>
                        {onRemoveGoalFund && (
                          <span aria-hidden="true" className="size-9 shrink-0" />
                        )}
                      </div>
                      {goal.funds.map((fund) => (
                        <GoalFundEditRow
                          key={fund.id}
                          fund={fund}
                          onUpdate={
                            onUpdateGoalFund
                              ? (input) => onUpdateGoalFund(goal.id, fund.id, input)
                              : undefined
                          }
                          onRemove={
                            onRemoveGoalFund ? () => onRemoveGoalFund(goal.id, fund.id) : undefined
                          }
                        />
                      ))}
                      <TotalMonthlySipRow totalMonthlySip={goal.totalMonthlySip} />
                    </div>
                  )}

                  {onAddGoalFund &&
                    (addingFund ? (
                      <form
                        onSubmit={onSubmitAddFund}
                        className="flex flex-col gap-2 rounded-md border p-2"
                      >
                        <Field>
                          <FieldLabel htmlFor="new-fund-name">Fund name</FieldLabel>
                          <Input
                            id="new-fund-name"
                            placeholder="e.g. HDFC Balanced Advantage Fund"
                            {...addFundForm.register('fundName')}
                          />
                          <FieldError errors={[addFundForm.formState.errors.fundName]} />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="new-fund-sip">Monthly SIP (₹)</FieldLabel>
                          <Input
                            id="new-fund-sip"
                            type="number"
                            inputMode="numeric"
                            placeholder="e.g. 5000"
                            {...addFundForm.register('monthlySip')}
                          />
                          <FieldError errors={[addFundForm.formState.errors.monthlySip]} />
                        </Field>
                        <div className="flex gap-2">
                          <Button
                            type="submit"
                            size="sm"
                            disabled={addFundForm.formState.isSubmitting}
                          >
                            {addFundForm.formState.isSubmitting ? 'Adding…' : 'Add fund'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setAddingFund(false)
                              addFundForm.reset({ fundName: '', monthlySip: '' })
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="w-fit"
                        onClick={() => setAddingFund(true)}
                      >
                        <Plus className="size-3.5" />
                        Add fund
                      </Button>
                    ))}

                  <form onSubmit={onSubmitNote} className="flex flex-col gap-2">
                    <Field>
                      <FieldLabel htmlFor="goal-plan-note">
                        Note for the client (optional)
                      </FieldLabel>
                      <Input
                        id="goal-plan-note"
                        placeholder="Any context to share"
                        {...noteForm.register('planNote')}
                      />
                    </Field>
                    <div className="flex gap-2">
                      <Button type="submit" size="sm" disabled={noteForm.formState.isSubmitting}>
                        {noteForm.formState.isSubmitting ? 'Saving…' : 'Save note'}
                      </Button>
                      {hasPlan && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingPlan(false)}
                        >
                          Done
                        </Button>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {onRemoveGoal && (
        <AlertDialog
          open={confirmingRemove}
          onOpenChange={(nextOpen) => !nextOpen && setConfirmingRemove(false)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove your {GOAL_KEY_LABEL[goal.key]} goal?</AlertDialogTitle>
              <AlertDialogDescription>This can&apos;t be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmRemove}>Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  )
}

function BuildProfileDialog({
  buildProfile,
  open,
  onOpenChange,
  onSubmit: onSubmitProp,
}: {
  buildProfile: CaseBuildProfile | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: UpsertCaseBuildProfileInput) => Promise<void>
}) {
  const form = useForm<
    z.input<typeof upsertBuildProfileSchema>,
    unknown,
    UpsertBuildProfileFormValues
  >({
    resolver: zodResolver(upsertBuildProfileSchema),
    mode: 'onTouched',
    values: {
      monthlyIncome: buildProfile?.monthlyIncome ?? '',
      rentEmi: buildProfile?.rentEmi ?? '',
      utilitiesGroceries: buildProfile?.utilitiesGroceries ?? '',
      transportLifestyle: buildProfile?.transportLifestyle ?? '',
      otherExpenses: buildProfile?.otherExpenses ?? '',
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await onSubmitProp(values)
    onOpenChange(false)
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>{buildProfile ? 'Edit' : 'Add'} financial profile</DialogTitle>
            <DialogDescription>This investor&apos;s monthly income and expenses.</DialogDescription>
          </DialogHeader>
          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="bp-income">Monthly income (take-home)</FieldLabel>
              <Input
                id="bp-income"
                type="number"
                inputMode="numeric"
                placeholder="e.g. 90000"
                {...form.register('monthlyIncome')}
              />
              <FieldError errors={[form.formState.errors.monthlyIncome]} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field>
                <FieldLabel htmlFor="bp-rent">Rent / EMI</FieldLabel>
                <Input
                  id="bp-rent"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 25000"
                  {...form.register('rentEmi')}
                />
                <FieldError errors={[form.formState.errors.rentEmi]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="bp-utilities">Utilities & groceries</FieldLabel>
                <Input
                  id="bp-utilities"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 15000"
                  {...form.register('utilitiesGroceries')}
                />
                <FieldError errors={[form.formState.errors.utilitiesGroceries]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="bp-transport">Transport & lifestyle</FieldLabel>
                <Input
                  id="bp-transport"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 8000"
                  {...form.register('transportLifestyle')}
                />
                <FieldError errors={[form.formState.errors.transportLifestyle]} />
              </Field>
              <Field>
                <FieldLabel htmlFor="bp-other">Loans, cards & other</FieldLabel>
                <Input
                  id="bp-other"
                  type="number"
                  inputMode="numeric"
                  placeholder="e.g. 10000"
                  {...form.register('otherExpenses')}
                />
                <FieldError errors={[form.formState.errors.otherExpenses]} />
              </Field>
            </div>
          </FieldGroup>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
